<template>
  <b-modal
    :visible="visible"
    :title="getModalTitle()"
    size="xl"
    hide-footer
    @hidden="$emit('close')"
  >
    <div v-if="data">
      <div
        v-if="data.checkpoint"
        class="checkpoint-track-meta mb-2"
      >
        <span class="track-meta-item">
          <strong>Точка:</strong>
          <code class="track-meta-code">{{ data.checkpoint.point }}</code>
        </span>
        <span
          v-if="backupPointLabel"
          class="track-meta-item"
        >
          <strong>Резерв:</strong>
          <code class="track-meta-code">{{ backupPointLabel }}</code>
        </span>
      </div>

      <div
        v-if="data.checkpoint"
        class="current-result-banner mb-3"
      >
        <div class="d-flex flex-wrap align-items-center">
          <strong>В результатах сейчас:</strong>
          <span class="current-result-time ml-2">{{ displayedGridTime }}</span>
          <b-badge
            v-if="data.checkpoint.isOverridden"
            variant="warning"
            class="ml-2"
          >
            Ручная правка
          </b-badge>
          <b-badge
            v-if="hasSelectedReading"
            variant="success"
            class="ml-2"
          >
            Назначено: 1
          </b-badge>
          <b-badge
            v-else
            variant="secondary"
            class="ml-2"
          >
            Назначено: 0
          </b-badge>
          <b-badge
            v-if="totalCandidateCount > 0"
            variant="light"
            class="ml-2 border"
          >
            Альтернатив: {{ totalCandidateCount }}
          </b-badge>
        </div>
        <small
          v-if="!hasSelectedReading && totalCandidateCount === 0"
          class="text-muted d-block mt-1"
        >
          Считываний на этой точке нет. Можно ввести время вручную или добавить виртуальное считывание во вкладках ниже.
        </small>
        <small
          v-else-if="!hasSelectedReading && totalCandidateCount > 0"
          class="text-muted d-block mt-1"
        >
          Время не назначено. Выберите подходящий вариант ниже или введите вручную.
        </small>
      </div>

      <div
        v-if="data.checkpoint"
        class="reader-code-banner mb-3"
      >
        <strong>Код считывателя:</strong>
        <code
          v-if="readerCodeForBanner.code"
          class="reader-code-value"
        >{{ readerCodeForBanner.code }}</code>
        <span
          v-else
          class="text-muted"
        >не указан в данных</span>
      </div>

      <div
        v-if="data.checkpoint && data.checkpoint.isOverridden"
        class="mb-3"
      >
        <b-button
          variant="outline-danger"
          size="sm"
          :disabled="isRemovingOverride"
          @click="confirmRemoveOverride"
        >
          Снять ручное время
        </b-button>
        <small class="text-muted d-block mt-1">
          Восстановить время по исходным считываниям (применится сразу на сервере).
        </small>
      </div>

      <b-tabs>
        <!-- Tab 1: Alternative readings -->
        <b-tab active>
          <template #title>
            <span v-b-tooltip.hover.top="'Просмотр всех считываний на этом чекпоинте и выбор альтернативного'">
              Выбрать другое считывание
            </span>
          </template>

          <!-- Rich block when API returned selectedReading (in-memory analysis) -->
          <div
            v-if="hasSelectedReading"
            class="mb-3"
          >
            <h6 class="section-title">
              Назначенное считывание
            </h6>
            <p class="text-muted small mb-2">
              Это считывание сейчас отображается в таблице результатов.
            </p>
            <div class="reading-card selected-reading">
              <div class="reading-header reading-header--compact">
                <div class="time-display time-display--compact">
                  <div class="time-relative-main">
                    <span class="time-label">Отн.</span>
                    <strong>{{ data.checkpoint.selectedReading.timestamp }}</strong>
                  </div>
                  <div
                    v-if="data.checkpoint.selectedReading.absoluteTimestamp"
                    class="time-absolute-main"
                  >
                    <span class="time-label">UTC</span>
                    <span>{{ formatAbsoluteTime(data.checkpoint.selectedReading.absoluteTimestamp) }}</span>
                  </div>
                  <div
                    v-if="data.checkpoint.selectedReading.absoluteTimestamp"
                    class="time-local-main"
                  >
                    <span class="time-label">{{ getTimezoneLabel() }}</span>
                    <span>{{ formatLocalDateTime(data.checkpoint.selectedReading.absoluteTimestamp) }}</span>
                  </div>
                </div>
                <div class="reading-header-actions">
                  <b-badge variant="success">
                    {{ data.checkpoint.selectedReading.splitTime }}
                  </b-badge>
                  <b-badge
                    variant="success"
                    class="ml-1"
                  >
                    ✓ Выбрано
                  </b-badge>
                </div>
              </div>

              <b-button
                v-b-toggle="'selected-reading-details'"
                size="sm"
                variant="link"
                class="details-toggle px-0"
              >
                Подробнее (чип, RSSI…)
              </b-button>
              <b-collapse
                id="selected-reading-details"
                class="reading-collapse"
              >
                <div class="reading-details reading-details--compact">
                  <div class="meta-grid">
                    <template v-if="data.checkpoint.selectedReading.code">
                      <div class="meta-cell">
                        <span class="meta-label">Код чипа</span>
                        <code>{{ data.checkpoint.selectedReading.code }}</code>
                      </div>
                    </template>
                    <template v-if="data.checkpoint.selectedReading.epc">
                      <div class="meta-cell meta-cell--wide">
                        <span class="meta-label">EPC</span>
                        <code class="small-code">{{ data.checkpoint.selectedReading.epc }}</code>
                      </div>
                    </template>
                    <template v-if="data.checkpoint.selectedReading.rssi">
                      <div class="meta-cell">
                        <span class="meta-label">RSSI</span>
                        <b-badge :variant="getRssiVariant(data.checkpoint.selectedReading.rssi)">
                          {{ data.checkpoint.selectedReading.rssi }} dBm
                        </b-badge>
                      </div>
                    </template>
                    <template v-if="data.checkpoint.selectedReading.antena">
                      <div class="meta-cell">
                        <span class="meta-label">Код считывателя (антенна)</span>
                        <span>{{ data.checkpoint.selectedReading.antena }}</span>
                      </div>
                    </template>
                    <template v-if="data.checkpoint.selectedReading.num">
                      <div class="meta-cell">
                        <span class="meta-label">Номер</span>
                        <span>{{ data.checkpoint.selectedReading.num }}</span>
                      </div>
                    </template>
                  </div>
                </div>
              </b-collapse>

              <div
                v-if="getDisplayReason(data.checkpoint.selectedReading)"
                class="reading-reason reading-reason--compact"
              >
                <small class="text-muted">{{ getDisplayReason(data.checkpoint.selectedReading) }}</small>
              </div>
            </div>
          </div>

          <div
            v-if="!hasCheckpointCandidates"
            class="text-center text-muted py-4"
          >
            На этой точке нет других считываний — ни альтернативных, ни не назначенных. Используйте «Ввести время вручную» или «Виртуальное считывание», если время требуется.
          </div>

          <div v-else>
            <div
              v-for="sec in candidateSections"
              :key="sec.key"
              :class="sec.sectionClass"
            >
              <h6 class="section-title">
                {{ sec.title }}
              </h6>
              <p class="text-muted small mb-2">
                {{ sec.hint }}
              </p>
              <div :class="sec.listClass">
                <div
                  v-for="(option, idx) in sec.items"
                  :key="sec.key + '-' + idx"
                  class="reading-card"
                  :class="{
                    'selected-reading': isAppliedOption(option),
                    'reading-card--inactive': isAppliedOption(option),
                    'reading-card--rejected': isRejectedCandidateCard(option),
                  }"
                >
                  <div class="reading-header reading-header--compact">
                    <div class="time-display time-display--compact">
                      <div class="time-relative-main">
                        <span class="time-label">Отн.</span>
                        <strong>{{ option.timestamp }}</strong>
                      </div>
                      <div
                        v-if="option.absoluteTimestamp"
                        class="time-absolute-main"
                      >
                        <span class="time-label">UTC</span>
                        <span>{{ formatAbsoluteTime(option.absoluteTimestamp) }}</span>
                      </div>
                      <div
                        v-if="option.absoluteTimestamp"
                        class="time-local-main"
                      >
                        <span class="time-label">{{ getTimezoneLabel() }}</span>
                        <span>{{ formatLocalDateTime(option.absoluteTimestamp) }}</span>
                      </div>
                    </div>
                    <div class="reading-header-actions">
                      <b-badge :variant="isAppliedOption(option) ? 'success' : 'secondary'">
                        {{ option.splitTime }}
                      </b-badge>
                      <b-badge
                        v-if="isAppliedOption(option)"
                        variant="success"
                        class="ml-1"
                      >
                        Сейчас в таблице
                      </b-badge>
                      <b-badge
                        v-else-if="option.fromRawOnly"
                        variant="light"
                        class="ml-1 text-muted border"
                      >
                        Сырое
                      </b-badge>
                      <b-badge
                        v-else
                        variant="secondary"
                        class="ml-1"
                      >
                        Анализ
                      </b-badge>
                      <b-button
                        v-if="optionCanSelectAsAlternative(option)"
                        v-b-tooltip.hover.left="option.fromRawOnly ? rawSelectTooltip : undefined"
                        size="sm"
                        variant="primary"
                        class="ml-2"
                        @click="selectAlternative(option)"
                      >
                        Использовать это время
                      </b-button>
                      <span
                        v-else-if="isAppliedOption(option)"
                        v-b-tooltip.hover.left="rawAlreadyInResultTooltip(option)"
                        class="d-inline-block ml-2"
                      >
                        <b-button
                          size="sm"
                          variant="outline-secondary"
                          disabled
                        >
                          Уже используется
                        </b-button>
                      </span>
                      <span
                        v-else-if="option.fromRawOnly && !getOverrideTimeStringForOption(option)"
                        v-b-tooltip.hover.left="rawViewOnlyTooltip"
                        class="d-inline-block ml-2"
                      >
                        <b-button
                          size="sm"
                          variant="outline-secondary"
                          disabled
                        >
                          Только просмотр
                        </b-button>
                      </span>
                    </div>
                  </div>

                  <b-button
                    v-b-toggle="'alt-reading-details-' + sec.key + '-' + idx"
                    size="sm"
                    variant="link"
                    class="details-toggle px-0"
                    @click.stop
                  >
                    Подробнее (чип, RSSI…)
                  </b-button>
                  <b-collapse
                    :id="'alt-reading-details-' + sec.key + '-' + idx"
                    class="reading-collapse"
                  >
                    <div class="reading-details reading-details--compact">
                      <div class="meta-grid">
                        <template v-if="option.code">
                          <div class="meta-cell">
                            <span class="meta-label">Код чипа</span>
                            <code>{{ option.code }}</code>
                          </div>
                        </template>
                        <template v-if="option.epc">
                          <div class="meta-cell meta-cell--wide">
                            <span class="meta-label">EPC</span>
                            <code class="small-code">{{ option.epc }}</code>
                          </div>
                        </template>
                        <template v-if="option.rssi">
                          <div class="meta-cell">
                            <span class="meta-label">RSSI</span>
                            <b-badge
                              :variant="getRssiVariant(option.rssi)"
                              class="small"
                            >
                              {{ option.rssi }} dBm
                            </b-badge>
                          </div>
                        </template>
                        <template v-if="option.antena">
                          <div class="meta-cell">
                            <span class="meta-label">Код считывателя (антенна)</span>
                            <span class="small">{{ option.antena }}</span>
                          </div>
                        </template>
                        <template v-if="option.num">
                          <div class="meta-cell">
                            <span class="meta-label">Номер</span>
                            <span class="small">{{ option.num }}</span>
                          </div>
                        </template>
                      </div>
                    </div>
                  </b-collapse>

                  <div
                    v-if="getDisplayReason(option)"
                    class="reading-reason reading-reason--compact"
                  >
                    <small class="text-muted">{{ getDisplayReason(option) }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Tab 2: Manual time entry -->
        <b-tab>
          <template #title>
            <span v-b-tooltip.hover.top="'Ввести время вручную в формате HH:MM:SS или относительно (+30, -15 секунд)'">
              Ввести время вручную
            </span>
          </template>
          <b-form @submit.prevent="saveManualTime">
            <b-form-group
              label="Время"
              description="Формат: HH:MM:SS.mmm или +/-секунды (например: +30 или -15)"
            >
              <b-form-input
                v-model="manualTime"
                placeholder="00:05:30.42 или +30"
                required
              />
            </b-form-group>

            <b-form-group label="Причина корректировки">
              <b-form-textarea
                v-model="manualReason"
                placeholder="Укажите причину ручного ввода времени"
                rows="3"
              />
            </b-form-group>

            <div class="text-right">
              <b-button
                variant="outline-secondary"
                class="mr-2"
                @click="$emit('close')"
              >
                Отмена
              </b-button>
              <b-button
                type="submit"
                variant="primary"
              >
                Сохранить время
              </b-button>
            </div>
          </b-form>
        </b-tab>

        <!-- Tab 3: Virtual reading -->
        <b-tab>
          <template #title>
            <span v-b-tooltip.hover.top="'Добавить виртуальное считывание с полными данными чипа. Полезно для ручного ввода финиша.'">
              Добавить виртуальное считывание
            </span>
          </template>
          <virtual-reading-form
            :checkpoint="data.checkpoint"
            :participant="data.participant"
            @save="handleVirtualReading"
            @cancel="$emit('close')"
          />
        </b-tab>
      </b-tabs>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BTabs,
  BTab,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BBadge,
  BCollapse,
  VBTooltip,
  VBToggle,
} from 'bootstrap-vue'
import VirtualReadingForm from './VirtualReadingForm.vue'
import TimezoneMixin from '../../mixins/timezoneMixin'
import { translateReasonCode, READING_REASON_CODES } from '../../utils/reason-codes'
import { buildRawSupplementOptionsForCheckpoint, ABS_MATCH_SLACK_MS } from '../../utils/checkpoint-cell-readings'

/** Parse HH:MM:SS.mmm or MM:SS.mmm to milliseconds (same basis as ResultsGrid / ParticipantDetail). */
function parseDurationToMs(str) {
  if (str == null || str === '') return null
  const s = String(str).trim()
  if (!s) return null
  const parts = s.split(':').map(p => p.trim())
  if (parts.length === 3) {
    const h = parseInt(parts[0], 10) || 0
    const m = parseInt(parts[1], 10) || 0
    const secParts = parts[2].split('.')
    const sec = parseInt(secParts[0], 10) || 0
    const frac = secParts[1] != null ? String(secParts[1]).replace(/\D/g, '') : ''
    const ms = parseInt(`${frac}000`.slice(0, 3), 10) || 0
    return ((h * 60 + m) * 60 + sec) * 1000 + ms
  }
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10) || 0
    const secParts = parts[1].split('.')
    const sec = parseInt(secParts[0], 10) || 0
    const frac = secParts[1] != null ? String(secParts[1]).replace(/\D/g, '') : ''
    const ms = parseInt(`${frac}000`.slice(0, 3), 10) || 0
    return (m * 60 + sec) * 1000 + ms
  }
  return null
}

export default {
  name: 'CheckpointEditor',
  components: {
    BModal,
    BTabs,
    BTab,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BBadge,
    BCollapse,
    VirtualReadingForm,
  },
  directives: {
    'b-tooltip': VBTooltip,
    'b-toggle': VBToggle,
  },
  mixins: [TimezoneMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: null,
    },
    isRemovingOverride: {
      type: Boolean,
      default: false,
    },
    /** Track checkpoint rows from race config (point, lap, backupPointAlias, …). */
    checkpoints: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      manualTime: '',
      manualReason: '',
      rawSelectTooltip:
        'Фиксирует время этого считывания в результате (как при ручной правке). Проверьте корректность для протокола.',
      rawViewOnlyTooltip:
        'Нет однозначной абсолютной или относительной метки для сохранения. Укажите время вручную на соседней вкладке.',
    }
  },
  computed: {
    hasSelectedReading() {
      return this.data && this.data.checkpoint && this.data.checkpoint.selectedReading
    },

    trackConfigForCurrentCheckpoint() {
      const cp = this.data?.checkpoint
      const list = this.checkpoints || []
      if (!cp || !list.length) return null
      return list.find(
        c => c && c.point === cp.point && (c.lap || 0) === (cp.lap || 0),
      ) || null
    },

    /** Backup point code from track config (Резерв), only when set. */
    backupPointLabel() {
      const cfg = this.trackConfigForCurrentCheckpoint
      if (!cfg || cfg.backupPointAlias == null) return ''
      const b = String(cfg.backupPointAlias).trim()
      return b || ''
    },

    /** Reader / antenna id from current analyzer-selected row (ChipReading.antena). */
    selectedReaderCodeDisplay() {
      const sr = this.data?.checkpoint?.selectedReading
      if (!sr) return ''
      const a = sr.antena != null ? String(sr.antena).trim() : ''
      if (!a || a === 'manual') return ''
      return a
    },

    /**
     * Banner value: API selectedReading.antena, else first raw supplement labeled analyzer-selected with antena.
     */
    readerCodeForBanner() {
      const direct = this.selectedReaderCodeDisplay
      if (direct) return { code: direct }
      const opts = this.rawSupplementOptions || []
      for (let i = 0; i < opts.length; i++) {
        const opt = opts[i]
        const ru = this.getDisplayReason(opt)
        const byAnalyzer = /выбрано анализатором/i.test(ru)
          || String(opt.reasonCode || '') === READING_REASON_CODES.SELECTED_BY_ANALYZER
        if (byAnalyzer) {
          const ant = opt.antena != null ? String(opt.antena).trim() : ''
          if (ant) return { code: ant }
        }
      }
      return { code: '' }
    },

    /** Same fallback as ResultsGrid.getCheckpointTime (segment mode). */
    displayedGridTime() {
      const cp = this.data && this.data.checkpoint
      if (!cp) return '—'
      const t = cp.displayTime || cp.splitTime || cp.time
      return t && String(t).trim() !== '' ? t : '—'
    },

    /**
     * Raw chip rows for this checkpoint that are not already represented in selectedReading
     * or additionalOptions (by absolute time).
     */
    rawSupplementOptions() {
      const { data } = this
      if (!data || !data.participant || !data.checkpoint) return []
      return buildRawSupplementOptionsForCheckpoint(data.participant, data.checkpoint)
    },

    apiAlternativeOptions() {
      if (!this.data || !this.data.checkpoint) return []
      return this.data.checkpoint.additionalOptions || []
    },

    candidateSections() {
      const sections = []
      if (this.apiAlternativeOptions.length) {
        sections.push({
          key: 'api',
          title: `Альтернативные считывания · ${this.apiAlternativeOptions.length}`,
          hint:
            'Считывания, которые анализатор отсеял. Любое можно назначить вручную — таблица обновится сразу.',
          items: this.apiAlternativeOptions,
          sectionClass: 'candidate-section candidate-section--api',
          listClass: 'readings-list readings-list--api',
        })
      }
      if (this.rawSupplementOptions.length) {
        sections.push({
          key: 'raw',
          title: `Не назначенные считывания · ${this.rawSupplementOptions.length}`,
          hint:
            'События с оборудования, которые не попали в результат. При наличии корректного времени можно зафиксировать любое из них как итоговую отметку.',
          items: this.rawSupplementOptions,
          sectionClass: 'candidate-section candidate-section--raw',
          listClass: 'readings-list readings-list--raw',
        })
      }
      return sections
    },

    hasCheckpointCandidates() {
      return this.apiAlternativeOptions.length > 0 || this.rawSupplementOptions.length > 0
    },

    totalCandidateCount() {
      return this.apiAlternativeOptions.length + this.rawSupplementOptions.length
    },
  },
  methods: {
    getModalTitle() {
      if (!this.data || !this.data.checkpoint) return 'Редактирование чекпоинта'

      const cp = this.data.checkpoint
      return `Редактирование: ${cp.header || cp.point}`
    },

    isAppliedOption(option) {
      const cp = this.data && this.data.checkpoint
      if (!cp || !option) return false

      // Prefer absolute-time matching when available.
      // This prevents false positives when many candidates share "00:00:00.00".
      const absOpt = this.parseAbsoluteMsFromReadingOption(option)
      const absSelected = cp.selectedReading ? this.parseAbsoluteMsFromReadingOption(cp.selectedReading) : null
      if (absOpt != null && absSelected != null) {
        return Math.abs(absOpt - absSelected) <= ABS_MATCH_SLACK_MS
      }
      const absOverride = cp.overrideTimeString ? Date.parse(String(cp.overrideTimeString).trim()) : Number.NaN
      if (absOpt != null && !Number.isNaN(absOverride)) {
        return Math.abs(absOpt - absOverride) <= ABS_MATCH_SLACK_MS
      }

      if (option.fromRawOnly && this.hasSelectedReading) {
        const sr = cp.selectedReading
        if (this.readingEditorTimesMatchSelected(sr, option)) return true
      }

      if (option.fromRawOnly && this.isRawAnalyzerRowMatchingTable(option, cp)) {
        return true
      }

      if (option.fromRawOnly && cp.isOverridden && cp.overrideTimeString) {
        const want = this.getOverrideTimeStringForOption(option)
        const ov = String(cp.overrideTimeString).trim()
        if (want) {
          if (ov === String(want).trim()) return true
          const a = Date.parse(want)
          const b = Date.parse(ov)
          if (!Number.isNaN(a) && !Number.isNaN(b) && Math.abs(a - b) <= 1500) return true
        }
      }

      const optRel = parseDurationToMs(option.timestamp)
      const cpRel = parseDurationToMs(cp.time)
      // Use relative-time equality only when absolute timestamps are unavailable.
      if (Number.isNaN(absOverride) && absOpt == null && absSelected == null && optRel !== null && cpRel !== null && optRel === cpRel) return true

      const optSplit = parseDurationToMs(option.splitTime)
      const cpSplit = parseDurationToMs(cp.splitTime)
      const cpDisplay = parseDurationToMs(cp.displayTime)
      if (Number.isNaN(absOverride) && absOpt == null && absSelected == null && optSplit !== null) {
        if (cpSplit !== null && optSplit === cpSplit) return true
        if (cpDisplay !== null && optSplit === cpDisplay) return true
      }

      return false
    },

    isRejectedCandidateCard(option) {
      return !this.isAppliedOption(option)
    },

    optionCanSelectAsAlternative(option) {
      if (this.isAppliedOption(option)) return false
      if (!option.fromRawOnly) return true
      return this.getOverrideTimeStringForOption(option) != null
    },

    /** String for checkpoint_time / parseOverrideToMs: ISO preferred for raw feed rows. */
    getOverrideTimeStringForOption(option) {
      if (!option || !option.fromRawOnly) return null
      const abs = option.absoluteTimestamp != null ? String(option.absoluteTimestamp).trim() : ''
      if (abs) {
        const d = Date.parse(abs)
        if (!Number.isNaN(d)) return abs
      }
      const ts = option.timestamp != null ? String(option.timestamp).trim() : ''
      if (!ts || ts === '—') return null
      if (ts.includes('T')) {
        const d2 = Date.parse(ts)
        if (!Number.isNaN(d2)) return ts
      }
      if (parseDurationToMs(ts) !== null) return ts
      return null
    },

    selectAlternative(option) {
      const timestamp = option.fromRawOnly
        ? this.getOverrideTimeStringForOption(option)
        : option.timestamp
      if (!timestamp) return
      this.$emit('select-alternative', {
        checkpoint: this.data.checkpoint,
        participant: this.data.participant,
        timestamp,
      })
    },

    async confirmRemoveOverride() {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Удалить сохранённое ручное время для этого чекпоинта и пересчитать результаты по считываниям?',
        {
          title: 'Снять ручное время',
          okVariant: 'danger',
          okTitle: 'Снять',
          cancelTitle: 'Отмена',
        },
      )
      if (!ok) return
      this.$emit('remove-override', {
        participant: this.data.participant,
        checkpoint: this.data.checkpoint,
      })
    },

    saveManualTime() {
      if (!this.manualTime) return

      this.$emit('save-manual-time', {
        checkpoint: this.data.checkpoint,
        participant: this.data.participant,
        time: this.manualTime,
        reason: this.manualReason,
      })

      this.manualTime = ''
      this.manualReason = ''
    },

    handleVirtualReading(data) {
      this.$emit('save-virtual-reading', data)
    },

    getDisplayReason(option) {
      if (!option) return ''
      return translateReasonCode(option.reasonCode, option.reason || '')
    },

    rawAlreadyInResultTooltip(option) {
      if (option && option.fromRawOnly) {
        return 'Та же отметка времени уже в результате (выбрана анализатором). Повторное сохранение создаст явный override в конфигурации.'
      }
      return 'Это время уже отображается в таблице для данного чекпоинта.'
    },

    parseAbsoluteMsFromReadingOption(opt) {
      if (!opt) return null
      const keys = ['absoluteTimestamp', 'timestamp']
      for (let i = 0; i < keys.length; i++) {
        const v = opt[keys[i]]
        if (v != null && v !== '') {
          const s = String(v).trim()
          if (s.includes('T') || (s.length >= 10 && s[4] === '-' && s[7] === '-')) {
            const t = Date.parse(s)
            if (!Number.isNaN(t)) return t
          }
        }
      }
      return null
    },

    readingEditorTimesMatchSelected(sr, option) {
      if (!sr || !option) return false
      const absSr = this.parseAbsoluteMsFromReadingOption(sr)
      const absOpt = this.parseAbsoluteMsFromReadingOption(option)
      if (absSr != null && absOpt != null) {
        return Math.abs(absSr - absOpt) <= ABS_MATCH_SLACK_MS
      }
      const relSr = parseDurationToMs(sr.timestamp)
      const relOpt = parseDurationToMs(option.timestamp)
      if (relSr != null && relOpt != null) return relSr === relOpt
      return false
    },

    /** Raw row marked as analyzer pick and same time as shown in grid / selectedReading. */
    isRawAnalyzerRowMatchingTable(option, cp) {
      if (!option?.fromRawOnly || !cp) return false
      const ru = this.getDisplayReason(option)
      const byAnalyzer = /выбрано анализатором/i.test(ru)
        || String(option.reasonCode || '') === READING_REASON_CODES.SELECTED_BY_ANALYZER
      if (!byAnalyzer) return false
      if (this.hasSelectedReading && this.readingEditorTimesMatchSelected(cp.selectedReading, option)) return true
      return this.rawOptionTimeMatchesCheckpointGrid(option, cp)
    },

    rawOptionTimeMatchesCheckpointGrid(option, cp) {
      const absOpt = this.parseAbsoluteMsFromReadingOption(option)
      if (absOpt != null && cp.selectedReading) {
        const absSr = this.parseAbsoluteMsFromReadingOption(cp.selectedReading)
        if (absSr != null && Math.abs(absSr - absOpt) <= ABS_MATCH_SLACK_MS) return true
      }
      const gridStrings = [cp.displayTime, cp.splitTime, cp.time, this.displayedGridTime]
      const gridMs = gridStrings
        .map(x => (x != null && String(x).trim() !== '' && String(x).trim() !== '—'
          ? parseDurationToMs(String(x).trim())
          : null))
        .filter(v => v != null)
      const optRel = parseDurationToMs(option.timestamp)
      if (optRel != null && gridMs.some(g => g === optRel)) return true
      return false
    },

    getRssiVariant(rssi) {
      const rssiValue = parseInt(rssi, 10)

      if (rssiValue > -60) return 'success'
      if (rssiValue > -70) return 'info'
      if (rssiValue > -80) return 'warning'
      return 'danger'
    },

    formatAbsoluteTime(timestamp) {
      if (!timestamp) return ''

      try {
        const date = new Date(timestamp)
        const year = date.getUTCFullYear()
        const month = String(date.getUTCMonth() + 1).padStart(2, '0')
        const day = String(date.getUTCDate()).padStart(2, '0')
        const hours = String(date.getUTCHours()).padStart(2, '0')
        const minutes = String(date.getUTCMinutes()).padStart(2, '0')
        const seconds = String(date.getUTCSeconds()).padStart(2, '0')
        const ms = String(date.getUTCMilliseconds()).padStart(3, '0')

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}`
      } catch (e) {
        return timestamp
      }
    },
  },
}
</script>

<style scoped>
.checkpoint-track-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.65rem 1rem;
  font-size: 0.875rem;
  color: #5e5873;
}

.track-meta-item {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
}

.track-meta-code {
  font-size: 0.85rem;
  padding: 0.08rem 0.35rem;
  background: #f6f6f9;
  border: 1px solid #ebe9f1;
  border-radius: 0.25rem;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #5e5873;
}

.current-result-banner {
  padding: 0.5rem 0.75rem;
  background: #f8f7fd;
  border: 1px solid #d0cce8;
  border-radius: 0.358rem;
  font-size: 0.9rem;
}

.current-result-time {
  margin-left: 0.35rem;
  font-weight: 600;
  color: #5e5873;
}

.reader-code-banner {
  padding: 0.45rem 0.75rem;
  font-size: 0.875rem;
  background: #f0fafc;
  border: 1px solid #bae8ea;
  border-radius: 0.358rem;
  color: #5e5873;
}

.reader-code-value {
  margin-left: 0.35rem;
  padding: 0.1rem 0.4rem;
  background: #fff;
  border: 1px solid #d8ecee;
  border-radius: 0.25rem;
  font-size: 0.85rem;
}

.readings-list {
  max-height: 480px;
  overflow-y: auto;
}

.candidate-section {
  margin-bottom: 1.25rem;
}

.candidate-section:last-child {
  margin-bottom: 0;
}

.candidate-section--raw {
  padding: 0.65rem 0.75rem;
  background: #faf8ff;
  border: 1px solid #e8e4f5;
  border-radius: 0.428rem;
}

.reading-card {
  padding: 0.5rem 0.65rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.428rem;
  margin-bottom: 0.5rem;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 4px 0 rgba(34, 41, 47, 0.04);
}

.reading-card--inactive {
  cursor: default;
}

.reading-card--rejected:not(.selected-reading) {
  background: #fafafa;
  border-color: #e4e1ec;
}

.reading-card--rejected:not(.selected-reading) .time-relative-main strong {
  color: #6e6b7b;
  font-weight: 500;
}

.reading-card:not(.reading-card--inactive):hover {
  border-color: #7367f0;
  box-shadow: 0 2px 8px 0 rgba(115, 103, 240, 0.12);
}

.reading-card.selected-reading {
  background: #f3f8f5;
  border: 2px solid #28c76f;
  box-shadow: 0 1px 6px 0 rgba(40, 199, 111, 0.15);
}

.reading-header--compact {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
  flex-wrap: wrap;
}

.reading-header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  flex-shrink: 0;
}

.time-display--compact {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.time-display {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.time-relative-main,
.time-absolute-main,
.time-local-main {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.time-relative-main {
  font-size: 0.95rem;
  font-weight: 600;
  color: #7367f0;
}

.time-absolute-main {
  font-size: 0.75rem;
  color: #6e6b7b;
}

.time-local-main {
  font-size: 0.75rem;
  color: #28c76f;
  font-weight: 500;
}

.time-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.3px;
  opacity: 0.75;
}

.details-toggle {
  font-size: 0.8rem;
  margin-bottom: 0.15rem;
}

.reading-collapse {
  margin-top: 0.25rem;
}

.reading-details--compact {
  padding: 0.4rem 0.5rem;
  background: #fafafa;
  border-radius: 0.3rem;
  font-size: 0.8rem;
  margin-top: 0;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(7.5rem, 1fr));
  gap: 0.35rem 0.5rem;
}

.meta-cell {
  min-width: 0;
}

.meta-cell--wide {
  grid-column: 1 / -1;
}

.meta-label {
  display: block;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #b9b9c3;
  margin-bottom: 0.1rem;
  font-weight: 600;
}

.reading-details code {
  background: #fff;
  padding: 0.1rem 0.35rem;
  border-radius: 0.25rem;
  font-size: 0.78rem;
  border: 1px solid #ebe9f1;
  color: #5e5873;
  font-family: 'Courier New', monospace;
}

.reading-details .small-code {
  font-size: 0.72rem;
  word-break: break-all;
}

.reading-details .small {
  font-size: 0.8rem;
  color: #6e6b7b;
}

.reading-reason--compact {
  margin-top: 0.35rem;
  padding: 0.35rem 0.45rem;
  background: #f3f2f7;
  border-radius: 0.3rem;
  font-size: 0.8rem;
  color: #6e6b7b;
  font-style: italic;
}
</style>
